import React from 'react';
import { FaShieldAlt, FaUserShield, FaLock, FaDatabase, FaGlobe, FaHandshake } from 'react-icons/fa';

const PrivacyPolicyPage = () => {
  return (
    <div className="privacy-policy-page">
      <div className="policy-container">
        <div className="policy-header">
          <FaShieldAlt className="policy-icon" />
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last updated: December 22, 2025</p>
          <p className="intro">
            At BroaderBusiness, we take your privacy seriously. This Privacy Policy explains how we collect, 
            use, protect, and share your information when you use our AI-powered marketing intelligence platform.
          </p>
        </div>

        <div className="policy-content">
          {/* Information We Collect */}
          <section className="policy-section">
            <div className="section-header">
              <FaDatabase className="section-icon" />
              <h2>1. Information We Collect</h2>
            </div>
            
            <div className="subsection">
              <h3>1.1 Information You Provide</h3>
              <ul>
                <li><strong>Account Information:</strong> Name, email address, business name, and contact details when you create an account</li>
                <li><strong>Business Data:</strong> Business location, industry type, products/services, and operational information</li>
                <li><strong>Campaign Content:</strong> Marketing materials, images, videos, and text content you create or upload</li>
                <li><strong>Payment Information:</strong> Billing details and payment method information (processed securely by third-party payment processors)</li>
                <li><strong>Support Communications:</strong> Messages and attachments when you contact our support team</li>
              </ul>
            </div>

            <div className="subsection">
              <h3>1.2 Information We Collect Automatically</h3>
              <ul>
                <li><strong>Usage Data:</strong> Features used, time spent, clicks, and interaction patterns</li>
                <li><strong>Device Information:</strong> Browser type, operating system, device identifiers, and IP address</li>
                <li><strong>Location Data:</strong> General geographic location for local business optimization features</li>
                <li><strong>Analytics Data:</strong> Performance metrics, conversion rates, and campaign effectiveness data</li>
                <li><strong>Log Data:</strong> Server logs including access times, pages viewed, and system errors</li>
              </ul>
            </div>

            <div className="subsection">
              <h3>1.3 Third-Party Platform Data</h3>
              <ul>
                <li><strong>Social Media Data:</strong> Profile information, follower counts, engagement metrics, and posting capabilities from connected Instagram, Facebook, X (Twitter), and Google My Business accounts</li>
                <li><strong>POS System Data:</strong> Sales data, inventory information, and customer transaction data from connected Toast, Shopify, and Square accounts</li>
                <li><strong>Google Maps Data:</strong> Business listings, competitor information, and local market data</li>
                <li><strong>AI Training Data:</strong> Anonymized and aggregated data to improve our AI recommendation algorithms</li>
              </ul>
            </div>
          </section>

          {/* How We Use Your Information */}
          <section className="policy-section">
            <div className="section-header">
              <FaUserShield className="section-icon" />
              <h2>2. How We Use Your Information</h2>
            </div>
            
            <div className="subsection">
              <h3>2.1 Core Platform Services</h3>
              <ul>
                <li>Provide AI-powered marketing insights and campaign recommendations</li>
                <li>Generate and optimize marketing content using artificial intelligence</li>
                <li>Manage and schedule social media posts across connected platforms</li>
                <li>Analyze local competition and market trends</li>
                <li>Track and report on campaign performance and analytics</li>
                <li>Sync data between your POS systems and marketing campaigns</li>
              </ul>
            </div>

            <div className="subsection">
              <h3>2.2 Account Management</h3>
              <ul>
                <li>Create and maintain your user account</li>
                <li>Process payments and manage billing</li>
                <li>Provide customer support and technical assistance</li>
                <li>Send important service updates and security notifications</li>
                <li>Authenticate your identity and prevent unauthorized access</li>
              </ul>
            </div>

            <div className="subsection">
              <h3>2.3 Improvement and Development</h3>
              <ul>
                <li>Improve our AI algorithms and recommendation engines</li>
                <li>Develop new features and enhance existing functionality</li>
                <li>Conduct research and analytics to understand user needs</li>
                <li>Detect and prevent fraud, abuse, and security incidents</li>
                <li>Comply with legal obligations and industry standards</li>
              </ul>
            </div>
          </section>

          {/* Information Sharing */}
          <section className="policy-section">
            <div className="section-header">
              <FaHandshake className="section-icon" />
              <h2>3. How We Share Your Information</h2>
            </div>
            
            <div className="subsection">
              <h3>3.1 With Your Consent</h3>
              <p>We share your information when you explicitly authorize us to do so, such as:</p>
              <ul>
                <li>Posting content to your connected social media accounts</li>
                <li>Accessing your POS system data for campaign optimization</li>
                <li>Sharing campaign performance data with your team members</li>
              </ul>
            </div>

            <div className="subsection">
              <h3>3.2 Service Providers</h3>
              <p>We work with trusted third-party service providers who help us operate our platform:</p>
              <ul>
                <li><strong>Cloud Infrastructure:</strong> Amazon Web Services (AWS) for secure data storage and processing</li>
                <li><strong>Payment Processing:</strong> Stripe and PayPal for secure payment transactions</li>
                <li><strong>Analytics:</strong> Google Analytics for website usage analysis (anonymized data)</li>
                <li><strong>Email Services:</strong> SendGrid for transactional emails and notifications</li>
                <li><strong>AI Services:</strong> OpenAI and Google AI for content generation and analysis</li>
              </ul>
            </div>

            <div className="subsection">
              <h3>3.3 Legal Requirements</h3>
              <p>We may disclose your information when required by law or to:</p>
              <ul>
                <li>Comply with legal processes, court orders, or regulatory requests</li>
                <li>Protect our rights, property, or safety, and that of our users</li>
                <li>Investigate and prevent fraud or security breaches</li>
                <li>Enforce our Terms of Service and other legal agreements</li>
              </ul>
            </div>

            <div className="subsection">
              <h3>3.4 Business Transfers</h3>
              <p>In the event of a merger, acquisition, or sale of assets, your information may be transferred to the new entity, subject to the same privacy protections.</p>
            </div>
          </section>

          {/* Data Security */}
          <section className="policy-section">
            <div className="section-header">
              <FaLock className="section-icon" />
              <h2>4. Data Security and Protection</h2>
            </div>
            
            <div className="subsection">
              <h3>4.1 Security Measures</h3>
              <ul>
                <li><strong>Encryption:</strong> All data is encrypted in transit using TLS 1.3 and at rest using AES-256</li>
                <li><strong>Access Controls:</strong> Strict employee access controls and multi-factor authentication</li>
                <li><strong>Regular Audits:</strong> Security assessments and penetration testing</li>
                <li><strong>Secure Infrastructure:</strong> SOC 2 Type II compliant cloud hosting</li>
                <li><strong>Data Backup:</strong> Regular automated backups with disaster recovery procedures</li>
              </ul>
            </div>

            <div className="subsection">
              <h3>4.2 Third-Party Platform Security</h3>
              <ul>
                <li>OAuth 2.0 authentication for social media platform connections</li>
                <li>Encrypted token storage with automatic refresh and expiration</li>
                <li>Minimal permission requests (only what's necessary for functionality)</li>
                <li>Regular security reviews of third-party integrations</li>
              </ul>
            </div>

            <div className="subsection">
              <h3>4.3 Data Breach Response</h3>
              <p>In the unlikely event of a security breach, we will:</p>
              <ul>
                <li>Notify affected users within 72 hours</li>
                <li>Provide details about what data was affected</li>
                <li>Take immediate action to secure the breach</li>
                <li>Offer assistance and resources to affected users</li>
                <li>Report to relevant authorities as required by law</li>
              </ul>
            </div>
          </section>

          {/* Your Rights and Controls */}
          <section className="policy-section">
            <div className="section-header">
              <FaUserShield className="section-icon" />
              <h2>5. Your Rights and Controls</h2>
            </div>
            
            <div className="subsection">
              <h3>5.1 Access and Portability</h3>
              <ul>
                <li><strong>Data Access:</strong> Request a copy of all personal data we hold about you</li>
                <li><strong>Data Export:</strong> Download your campaign data, analytics, and content in portable formats</li>
                <li><strong>Account Dashboard:</strong> View and manage your connected accounts and data sharing preferences</li>
              </ul>
            </div>

            <div className="subsection">
              <h3>5.2 Correction and Deletion</h3>
              <ul>
                <li><strong>Data Correction:</strong> Update or correct inaccurate personal information</li>
                <li><strong>Account Deletion:</strong> Delete your account and associated data permanently</li>
                <li><strong>Selective Deletion:</strong> Remove specific campaigns, content, or connected accounts</li>
                <li><strong>Data Retention:</strong> Control how long we retain your information</li>
              </ul>
            </div>

            <div className="subsection">
              <h3>5.3 Privacy Controls</h3>
              <ul>
                <li><strong>Cookie Management:</strong> Control cookie preferences and tracking settings</li>
                <li><strong>Marketing Communications:</strong> Opt out of promotional emails and notifications</li>
                <li><strong>Data Processing:</strong> Object to certain types of data processing</li>
                <li><strong>Third-Party Connections:</strong> Revoke access to connected social media and POS accounts</li>
              </ul>
            </div>
          </section>

          {/* Data Retention */}
          <section className="policy-section">
            <div className="section-header">
              <FaDatabase className="section-icon" />
              <h2>6. Data Retention</h2>
            </div>
            
            <div className="subsection">
              <p>We retain your information for different periods based on the type of data and legal requirements:</p>
              <ul>
                <li><strong>Account Data:</strong> Retained while your account is active and for 30 days after deletion</li>
                <li><strong>Campaign Data:</strong> Retained for 2 years or until you request deletion</li>
                <li><strong>Analytics Data:</strong> Aggregated and anonymized data may be retained indefinitely for service improvement</li>
                <li><strong>Payment Data:</strong> Billing information retained for 7 years for tax and legal compliance</li>
                <li><strong>Support Data:</strong> Support communications retained for 3 years</li>
                <li><strong>Log Data:</strong> System logs retained for 90 days for security and debugging purposes</li>
              </ul>
            </div>
          </section>

          {/* International Transfers */}
          <section className="policy-section">
            <div className="section-header">
              <FaGlobe className="section-icon" />
              <h2>7. International Data Transfers</h2>
            </div>
            
            <div className="subsection">
              <p>BroaderBusiness operates globally, and your data may be transferred to and processed in countries other than your own. We ensure adequate protection through:</p>
              <ul>
                <li><strong>Standard Contractual Clauses:</strong> EU-approved data transfer agreements</li>
                <li><strong>Adequacy Decisions:</strong> Transfers only to countries with adequate privacy protections</li>
                <li><strong>Certification Programs:</strong> Privacy Shield successor frameworks where applicable</li>
                <li><strong>Data Localization:</strong> Options to store data in specific geographic regions upon request</li>
              </ul>
            </div>
          </section>

          {/* Children's Privacy */}
          <section className="policy-section">
            <div className="section-header">
              <FaUserShield className="section-icon" />
              <h2>8. Children's Privacy</h2>
            </div>
            
            <div className="subsection">
              <p>BroaderBusiness is intended for business use by adults aged 18 and older. We do not knowingly collect personal information from children under 18. If you believe we have inadvertently collected such information, please contact us immediately at privacy@broaderbusiness.com.</p>
            </div>
          </section>

          {/* Cookie Policy */}
          <section className="policy-section">
            <div className="section-header">
              <FaDatabase className="section-icon" />
              <h2>9. Cookie Policy</h2>
            </div>
            
            <div className="subsection">
              <h3>9.1 Types of Cookies We Use</h3>
              <ul>
                <li><strong>Essential Cookies:</strong> Required for basic platform functionality and security</li>
                <li><strong>Performance Cookies:</strong> Help us understand how users interact with our platform</li>
                <li><strong>Functionality Cookies:</strong> Remember your preferences and settings</li>
                <li><strong>Analytics Cookies:</strong> Provide insights into platform usage and performance</li>
              </ul>
            </div>

            <div className="subsection">
              <h3>9.2 Cookie Management</h3>
              <p>You can control cookie settings through:</p>
              <ul>
                <li>Your browser settings to block or delete cookies</li>
                <li>Our cookie consent banner when you first visit</li>
                <li>Your account privacy settings for ongoing management</li>
              </ul>
            </div>
          </section>

          {/* Changes to This Policy */}
          <section className="policy-section">
            <div className="section-header">
              <FaShieldAlt className="section-icon" />
              <h2>10. Changes to This Privacy Policy</h2>
            </div>
            
            <div className="subsection">
              <p>We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. We will:</p>
              <ul>
                <li>Notify users of material changes via email and platform notifications</li>
                <li>Post the updated policy with a new "Last Updated" date</li>
                <li>Provide a summary of key changes when significant updates occur</li>
                <li>Allow a 30-day period for review before material changes take effect</li>
              </ul>
            </div>
          </section>

          {/* Contact Information */}
          <section className="policy-section contact-section">
            <div className="section-header">
              <FaHandshake className="section-icon" />
              <h2>11. Contact Us</h2>
            </div>
            
            <div className="contact-info">
              <p>If you have questions about this Privacy Policy or your personal data, please contact us:</p>
              
              <div className="contact-methods">
                <div className="contact-item">
                  <strong>Email:</strong> privacy@broaderbusiness.com
                </div>
                <div className="contact-item">
                  <strong>Mail:</strong> BroaderBusiness Privacy Team<br />
                  123 Business Street<br />
                  San Francisco, CA 94105
                </div>
                <div className="contact-item">
                  <strong>Data Protection Officer:</strong> dpo@broaderbusiness.com
                </div>
                <div className="contact-item">
                  <strong>EU Representative:</strong> eu-privacy@broaderbusiness.com
                </div>
              </div>
              
              <p className="response-time">We aim to respond to all privacy inquiries within 30 days.</p>
            </div>
          </section>
        </div>

        {/* Quick Reference */}
        <div className="policy-footer">
          <div className="quick-reference">
            <h3>Quick Reference</h3>
            <div className="reference-grid">
              <div className="reference-item">
                <FaUserShield className="ref-icon" />
                <span>Your data is encrypted and secure</span>
              </div>
              <div className="reference-item">
                <FaLock className="ref-icon" />
                <span>You control your privacy settings</span>
              </div>
              <div className="reference-item">
                <FaDatabase className="ref-icon" />
                <span>Data is used only for stated purposes</span>
              </div>
              <div className="reference-item">
                <FaGlobe className="ref-icon" />
                <span>International transfers are protected</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;